import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchAccessLogs } from "../services/api";
import { useCredentialsContext } from "../context/CredentialsContext";
import { useAuth } from "../context/AuthContext";
import "../styles/audit.css";

export default function AuditPage() {
    const navigate = useNavigate();
    const { credentials } = useCredentialsContext();
    const { authEnabled, role, roleLoading } = useAuth();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [offset, setOffset] = useState(0);
    const PAGE_SIZE = 50;

    const isInternal = role === "internal_staff";

    const credentialMap = useMemo(() => {
        const map = new Map();
        credentials.forEach((c) => map.set(String(c.id), c));
        return map;
    }, [credentials]);

    useEffect(() => {
        if (!authEnabled || !isInternal) {
            setLoading(false);
            return;
        }
        let isMounted = true;
        setLoading(true);
        fetchAccessLogs({ limit: PAGE_SIZE, offset })
            .then((data) => {
                if (!isMounted) return;
                setLogs(Array.isArray(data) ? data : []);
                setError(null);
            })
            .catch((err) => {
                if (!isMounted) return;
                setError(err?.message || "Unable to load audit logs.");
            })
            .finally(() => {
                if (!isMounted) return;
                setLoading(false);
            });
        return () => {
            isMounted = false;
        };
    }, [authEnabled, isInternal, offset]);

    if (!authEnabled) {
        return (
            <div className="audit-page">
                <div className="audit-card">
                    <h1>Audit</h1>
                    <p className="helper">
                        Supabase is not configured. Add your Supabase environment values to
                        use audit logs.
                    </p>
                    <button className="btn ghost" onClick={() => navigate("/")}>
                        Back to dashboard
                    </button>
                </div>
            </div>
        );
    }

    if (roleLoading) {
        return (
            <div className="audit-page">
                <div className="audit-card">
                    <h1>Audit</h1>
                    <p className="helper">Checking access...</p>
                </div>
            </div>
        );
    }

    if (!isInternal) {
        return (
            <div className="audit-page">
                <div className="audit-card">
                    <h1>Audit</h1>
                    <p className="helper">Audit logs are only available to internal staff.</p>
                    <button className="btn ghost" onClick={() => navigate("/")}>
                        Back to dashboard
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="audit-page">
            <div className="audit-card">
                <div className="audit-header">
                    <div>
                        <p className="eyebrow">Security</p>
                        <h1>Audit log</h1>
                        <p className="helper">
                            Recent password accesses and updates. Showing latest 200 entries.
                        </p>
                    </div>
                    <button className="btn ghost" onClick={() => navigate("/")}>
                        Back
                    </button>
                </div>

                {loading && <p>Loading logs…</p>}
                {error && <p className="error-message">{error}</p>}

                {!loading && !error && logs.length === 0 && (
                    <p>No audit entries yet.</p>
                )}

                {!loading && !error && logs.length > 0 && (
                    <>
                        <div className="audit-table">
                            <div className="audit-row header">
                                <span>When</span>
                                <span>Credential</span>
                                <span>Action</span>
                                <span>User ID</span>
                                <span>IP</span>
                                <span>User agent</span>
                            </div>
                            {logs.map((log) => {
                                const credential = credentialMap.get(String(log.credential_id));
                                const label = credential
                                    ? `${credential.company || "Unknown"} · ${credential.provider || "Service"}`
                                    : log.credential_id;
                                const when = log.accessed_at
                                    ? new Date(log.accessed_at).toLocaleString()
                                    : "";
                                return (
                                    <div className="audit-row" key={`${log.id}-${log.accessed_at}`}>
                                        <span>{when}</span>
                                        <span>{label}</span>
                                        <span className="action-chip">{log.action}</span>
                                        <span className="muted">{log.accessed_by}</span>
                                        <span className="muted">{log.ip || "—"}</span>
                                        <span className="muted agent-cell">{log.user_agent || "—"}</span>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="audit-pagination">
                            <button
                                className="btn ghost"
                                onClick={() => setOffset((prev) => Math.max(prev - PAGE_SIZE, 0))}
                                disabled={loading || offset === 0}
                            >
                                Newer
                            </button>
                            <button
                                className="btn ghost"
                                onClick={() => setOffset((prev) => prev + PAGE_SIZE)}
                                disabled={loading || logs.length < PAGE_SIZE}
                            >
                                Older
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
